import { EventEmitter } from "eventemitter3";
import { SoundManager } from "./SoundManager";
import { SoundGroup } from "./SoundGroup";
import { SoundConfig } from "./Config";
import { BaseSound } from "./BaseSound";

export class Sound extends EventEmitter {
    /**
     *
     * @param config
     */
    public static setConfigDefaults(config?: SoundConfig): SoundConfig {
        config = config || { src: "" };
        config.src = config.src || "";
        config.tags = config.tags || [];
        config.name = typeof config.name === "string" ? config.name : "";
        config.rate = typeof config.rate === "number" ? config.rate : 1;
        config.channel = typeof config.channel === "string" ? config.channel : "";
        config.group = typeof config.group === "string" ? config.group : "main";
        config.interruptSelf =
            typeof config.interruptSelf === "boolean" ? config.interruptSelf : false;
        config.interruptGroup =
            typeof config.interruptGroup === "boolean" ? config.interruptGroup : false;
        config.loop = !!config.loop;

        return config;
    }

    /**
     */
    public poly: number = 1;

    /**
     */
    private polyIndex: number = 0;

    /**
     */
    public ids: number[] = [];

    /**
     */
    public channel: string;

    /**
     */
    public manager: SoundManager;

    /**
     */
    public config: SoundConfig;

    /**
     */
    public group: SoundGroup;

    /**
     */
    public interruptSelf: boolean;

    /**
     */
    public interruptGroup: boolean;

    /**
     */
    public baseSound: BaseSound;

    /**
     */
    public name: string;

    /**
     */
    public autoLoad: boolean = true;

    /**
     */
    get state(): string {
        return this.baseSound.source.state();
    }

    /**
     */
    get loaded(): boolean {
        return this.state === "loaded";
    }

    /**
     */
    get stopped(): boolean {
        return !this.playing && !this.getPauseState();
    }

    /**
     */
    get duration(): number {
        if (typeof this.config.duration === "number") return this.config.duration;
        else return this.baseSound.source.duration();
    }

    /**
     */
    get durationMs(): number {
        return this.duration * 1000;
    }

    /**
     */
    get playing(): boolean {
        return this.baseSound.source.playing();
    }

    /**
     */
    get src(): string {
        return (this.baseSound.source as any)._src;
    }

    /**
     */
    get volume(): number {
        return this.baseSound.source.volume();
    }
    set volume(value) {
        (this.baseSound.source as any).volume(value);
    }

    /**
     */
    get rate(): number {
        return (this.baseSound.source as any)._rate;
    }
    set rate(value) {
        (this.baseSound.source as any).rate(value);
    }

    /**
     */
    get loop(): boolean {
        return (this.baseSound.source as any)._loop;
    }
    set loop(value: boolean) {
        (this.baseSound.source as any).loop(value);
    }

    /**
     */
    private _isPaused: boolean = false;
    private _isUnloaded: boolean = true;
    private _paused: boolean = false;
    get paused(): boolean {
        return this._paused;
    }
    set paused(value: boolean) {
        this._paused = value;
        this.updatePauseState();
    }

    /**
     */
    private _muted: boolean = false;
    get muted(): boolean {
        return this._muted;
    }
    set muted(value) {
        if (this._muted !== !!value) {
            this._muted = value;
            (this.baseSound.source as any).mute(value);
        }
    }

    /**
     */
    constructor(manager: SoundManager, baseSound: BaseSound, config?: SoundConfig) {
        super();
        this.manager = manager;
        this.baseSound = baseSound;
        if (config) {
            this.config = Sound.setConfigDefaults(config);
            this.parseConfig(config);
        }
        this.addEvents();
    }

    public getMuteState() {
        return this.manager.muted || this.group.muted || this.muted;
    }

    public updateMuteState() {
        const muted = this.getMuteState();
        this.baseSound.source.mute(muted);
    }

    public getPauseState() {
        return this.manager.paused || this.group.paused || this.paused;
    }

    public updatePauseState() {
        const paused = this.getPauseState();
        if (paused) {
            if (this.playing) {
                this._isPaused = true;
                for (let i = 0; i < this.ids.length; i++) {
                    this.baseSound.source.pause(this.ids[i]);
                }
            }
        } else if (this._isPaused) {
            this._isPaused = false;
            this.resume();
        }
    }

    /**
     */
    public parseConfig(config?: SoundConfig) {
        config = config || { src: "" };
        if (config.group) {
            this.group = this.manager.getGroup(config.group) || this.manager.groups.main;
        }
        this.name = this.getName();
        this.loop = !!this.config.loop;
        this.poly = this.config.poly || 1;
    }

    /**
     */
    private getName(): string {
        const src = typeof this.config.src === "string" ? this.config.src : this.config.src[0];
        return src //
            .slice(src.lastIndexOf("/") + 1, src.length)
            .replace(/\.[^/.]+$/, "");
    }

    /**
     */
    public play(config?: SoundConfig): this {
        if (this._isUnloaded && !this.autoLoad) return this;

        if (config) this.parseConfig(config);
        const group = this.group;
        const currentName = group.current && group.current.name;
        this._paused = false;

        if (group && group.config.poly === 1) {
            if (group.current) {
                if (this.group.interruptGroup && currentName !== this.name) {
                    this.manager.stopGroup(group.name);
                    group.current = this;
                    this._play();
                } else if (this.group.interruptSelf && currentName === this.name) {
                    group.current.stop();
                    group.current = this;
                    this._play();
                }
            } else {
                group.current = this;
                this._play();
            }
        } else {
            this._play();
        }

        SoundManager.info(`Playing sound: ${this.name}, ${this.ids}`);

        return this;
    }

    private _play() {
        if (this.ids.length < this.poly) {
            const name = this.config.spriteGroup ? this.name : undefined;
            const id = this.baseSound.source.play(name);
            if (typeof id === "number") this.ids.push(id);
        } else {
            const id = this.ids[this.polyIndex];
            this.baseSound.source.seek(0, id);
            this.baseSound.source.play(id);
        }

        this.polyIndex++;
        this.polyIndex %= this.poly;

        if (this.state !== "loaded" && this.autoLoad) {
            SoundManager.warn("Auto loading", this.name);
            this.load();
        }
    }

    public resume() {
        if (this._isUnloaded && !this.autoLoad) return this;

        for (let i = 0; i < this.ids.length; i++) {
            this.baseSound.source.play(this.ids[i]);
        }
    }

    public stop(): this {
        if (this._isUnloaded && !this.autoLoad) return this;

        if (this.ids.length > 0) {
            SoundManager.info(`Stop sound: ${this.name}`);
            if (this.group.current && this.group.current.name === this.name)
                this.group.current = undefined;
            this._isPaused = false;
            this.baseSound.source.stop();
            this.resetPoly();
        }
        return this;
    }

    private resetPoly() {
        this.ids = [];
        this.polyIndex = 0;
    }

    public load(): this {
        const state = this.baseSound.source.state();
        if (state !== "loaded") {
            SoundManager.info(`Load sound: ${this.name}`);
            if (state === "loading") {
                this.baseSound.source.once("load", () => this.emit("load"));
            } else {
                this.baseSound.source.once("load", () => this.emit("load"));
                this.baseSound.source.load();
            }
        } else this.emit("load");

        return this;
    }

    public unload(): this {
        SoundManager.info(`Unload sound: ${this.name}`);
        this._isUnloaded = true;
        let needsBaseUnload = true;
        for (const key in this.manager.cache) {
            const element = this.manager.cache[key];
            if (element.baseSound.getName() === this.baseSound.getName()) {
                if (!element._isUnloaded) needsBaseUnload = false;
            }
        }
        if (needsBaseUnload) {
            this.baseSound.source.unload();
        } else {
            this.stop();
        }
        this.resetPoly();
        return this;
    }

    /**
     */
    private addEvents() {
        this.baseSound.source.on("play", id => {
            if (this.ids.indexOf(id) !== -1) {
                this.manager.emitPlay(this, id);
                this.emit("play");
            }
        });
        this.baseSound.source.on("end", id => {
            if (this.ids.indexOf(id) !== -1) {
                if (!this.config.loop) {
                    this.ids = this.ids.filter(el => el !== id);
                    this.manager.emitEnd(this, id);
                    this.emit("end");
                }
            }
        });
        this.baseSound.source.on("stop", id => {
            if (this.ids.indexOf(id) !== -1) {
                this.ids = this.ids.filter(el => el !== id);
                this.manager.emitStop(this, id);
                this.emit("stop");
            }
        });
        this.baseSound.source.on("load", () => {
            this._isUnloaded = false;
            this.emit("load");
        });
    }
}
